import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get, sum } from 'lodash';
import { Typography } from '@mui/material';

import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { ChartBox } from '../molecules/chart-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';

import { Group } from '../organisms/group';

import { FormattedValue } from '../store/formatted-value';
import { FormattedStatus } from '../store/formatted-status';
import { useSelector } from '../store/utils';

import { useFormatNumber } from '../utils/format-number';
import { defaultYAxisDomainEndFrom } from '../utils/statistics';

const ElectricalGridPage = () => {
  const formatNumber = useFormatNumber();

  const value = useSelector((s) =>
    sum([
      get(s.variables, 'EM_INFO.Consumption[0]'),
      get(s.variables, 'EM_INFO.Consumption[1]'),
      get(s.variables, 'EM_INFO.Consumption[2]'),
    ])
  );

  const formattedValue = formatNumber(value, {
    style: 'unit',
    unit: 'kilowatt',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  const value2 = useSelector((s) =>
    get(s.variables, 'EM_ENERGY_CONSUMED_TOTAL')
  );

  const formattedValue2 = formatNumber(value2, {
    style: 'unit',
    unit: 'kilowatt-hour',
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });

  const value3 = useSelector((s) =>
    get(s.variables, 'EM_ENERGY_OVERFLOW_TOTAL')
  );

  const formattedValue3 = formatNumber(value3, {
    style: 'unit',
    unit: 'kilowatt-hour',
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="electrical_grid.status"
              defaultMessage="Stav distribuční sítě"
            />
          }
          subtitle={<FormattedStatus type="smartMeter" />}
          fullWidth
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="electrical_grid.statistics"
            defaultMessage="Statistiky nákupu"
          />
        }
      >
        <ChartBox
          variableId="EM_CONSUMED_GRAPH"
          yAxisDomainEndFrom={defaultYAxisDomainEndFrom}
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="electrical_grid.statistics_overflow"
            defaultMessage="Statistiky přetoku"
          />
        }
      >
        <ChartBox
          variableId="EM_OVERFLOW_GRAPH"
          yAxisDomainEndFrom={defaultYAxisDomainEndFrom}
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="electrical_grid.common"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="electrical_grid.common.power"
              defaultMessage="Aktuální odběr"
            />
          }
          value={formattedValue}
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="electrical_grid.common.power.phase_1"
                defaultMessage="Fáze 1:"
              />
              &nbsp;
              <FormattedValue
                variableId="EM_INFO.Consumption[0]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="electrical_grid.common.power.phase_2"
                defaultMessage="Fáze 2:"
              />
              &nbsp;
              <FormattedValue
                variableId="EM_INFO.Consumption[1]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="electrical_grid.common.power.phase_3"
                defaultMessage="Fáze 3:"
              />
              &nbsp;
              <FormattedValue
                variableId="EM_INFO.Consumption[2]"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
            </Typography>
          }
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="electrical_grid.common.total_consumption"
              defaultMessage="Celkový odběr ze sítě"
            />
          }
          value={formattedValue2}
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="electrical_grid.common.power_today"
                defaultMessage="Dnešní odběr ze sítě:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_EM_ENERGY_CONSUMED_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="electrical_grid.common.power_year"
                defaultMessage="Roční odběr ze sítě:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_EM_ENERGY_CONSUMED_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="electrical_grid.common.overflow"
              defaultMessage="Celkový přetok do sítě"
            />
          }
          value={formattedValue3}
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="electrical_grid.common.overflow_today"
                defaultMessage="Dnešní přetok do sítě:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_EM_ENERGY_OVERFLOW_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="electrical_grid.common.overflow_year"
                defaultMessage="Roční přetok do sítě:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_EM_ENERGY_OVERFLOW_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>
    </>
  );
};

export default ElectricalGridPage;
